// frontend/src/pages/SellerProfile.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Grid, Avatar, Chip, Rating,
  LinearProgress, Card, CardContent, CardMedia, CardActionArea,
  Tabs, Tab, CircularProgress, Alert, Stack
} from '@mui/material';
import {
  Star, LocationOn, Phone, Email, Verified, Home,
  TrendingUp, AccessTime, EmojiEvents
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const SellerProfile = () => {
  const { username } = useParams(); // Pode ser ID ou username
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sellerData, setSellerData] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchSellerProfile();
  }, [username]);

  const fetchSellerProfile = async () => {
    try {
      setLoading(true);
      
      // ✅ Tenta buscar por username direto
      let url = `${API_BASE_URL}/api/sellers/${username}/`;
      
      // ✅ Se username for um número (ID), busca todos e encontra pelo ID
      if (!isNaN(username)) {
        // Buscar lista de propriedades e encontrar o owner pelo ID
        const propsResponse = await axios.get(`${API_BASE_URL}/api/properties/`);
        const property = propsResponse.data.results.find(p => p.owner.id === parseInt(username));
        
        if (property && property.owner_username) {
          url = `${API_BASE_URL}/api/sellers/${property.owner_username}/`;
        } else {
          setError('Vendedor não encontrado');
          setLoading(false);
          return;
        }
      }
      
      const response = await axios.get(url);
      setSellerData(response.data);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar perfil:', err);
      setError('Vendedor não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return '#4caf50';
    if (rating >= 3.5) return '#ff9800';
    return '#f44336';
  };

  const getBadgeColor = (badgeType) => {
    const colors = {
      'bronze': '#cd7f32',
      'silver': '#c0c0c0',
      'gold': '#ffd700',
      'platinum': '#e5e4e2',
      'diamond': '#b9f2ff'
    };
    return colors[badgeType] || '#757575';
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const { profile, properties, reviews } = sellerData;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header do Perfil */}
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          {/* Avatar e Info Básica */}
          <Grid size={{ xs: 12, md: 3 }} sx={{ textAlign: 'center' }}>
            <Avatar
              src={profile.avatar}
              alt={profile.username}
              sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
            />
            {profile.is_verified && (
              <Chip
                icon={<Verified />}
                label="Verificado"
                color="primary"
                size="small"
                sx={{ mb: 1 }}
              />
            )}
            {profile.seller_badge && (
              <Chip
                icon={<EmojiEvents />}
                label={profile.seller_badge}
                sx={{
                  bgcolor: getBadgeColor(profile.seller_badge.toLowerCase()),
                  color: 'white',
                  fontWeight: 'bold'
                }}
                size="small"
              />
            )}
          </Grid>

          {/* Informações Principais */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              {profile.username}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Rating
                value={profile.average_rating}
                precision={0.1}
                readOnly
                size="large"
              />
              <Typography variant="h6" sx={{ ml: 1, color: getRatingColor(profile.average_rating) }}>
                {profile.average_rating.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({profile.total_reviews} {profile.total_reviews === 1 ? 'avaliação' : 'avaliações'})
              </Typography>
            </Box>

            {profile.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Phone sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{profile.phone}</Typography>
              </Box>
            )}

            {profile.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Email sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{profile.email}</Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <AccessTime sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Responde em até {profile.response_time_hours}h
              </Typography>
            </Box>
          </Grid>

          {/* Estatísticas */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <Home color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {properties.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Imóveis Ativos
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <TrendingUp color="success" sx={{ fontSize: 32 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {profile.total_properties_sold || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vendas
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Distribuição de Avaliações */}
        {reviews.count > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Distribuição de Avaliações
            </Typography>
            <Grid container spacing={1}>
              {[5, 4, 3, 2, 1].map((star) => (
                <Grid size={{ xs: 12 }} key={star}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ width: 80 }}>
                      {star} estrelas
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={reviews.count > 0 ? (reviews.stats[`${['one', 'two', 'three', 'four', 'five'][star - 1]}_star`] / reviews.count) * 100 : 0}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 4,
                        mx: 2,
                        bgcolor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getRatingColor(star)
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ width: 40, textAlign: 'right' }}>
                      {reviews.stats[`${['one', 'two', 'three', 'four', 'five'][star - 1]}_star`]}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Badges */}
        {profile.badges && profile.badges.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Conquistas
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {profile.badges.map((badge) => (
                <Chip
                  key={badge.id}
                  icon={<EmojiEvents />}
                  label={badge.badge_name}
                  sx={{
                    bgcolor: getBadgeColor(badge.badge_type),
                    color: 'white',
                    mb: 1
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Tabs - Imóveis e Avaliações */}
      <Paper elevation={3}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Imóveis (${properties.count})`} icon={<Home />} iconPosition="start" />
          <Tab label={`Avaliações (${reviews.count})`} icon={<Star />} iconPosition="start" />
        </Tabs>

        {/* Tab 0: Imóveis */}
        {currentTab === 0 && (
          <Box sx={{ p: 3 }}>
            {properties.count === 0 ? (
              <Alert severity="info">Este vendedor ainda não possui imóveis ativos.</Alert>
            ) : (
              <Grid container spacing={3}>
                {properties.results.map((property) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={property.id}>
                    <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardActionArea onClick={() => navigate(`/imoveis/${property.id}`)}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={property.main_image || '/placeholder_property.png'}
                          alt={property.title}
                        />
                        <CardContent>
                          <Typography variant="h6" gutterBottom noWrap>
                            {property.title}
                          </Typography>
                          <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                            {property.price_on_request ? 'Valor a Combinar' : formatPrice(property.price)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LocationOn sx={{ fontSize: 16, mr: 0.5 }} />
                            <Typography variant="body2">
                              {property.city}, {property.state}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {property.bedrooms > 0 && (
                              <Chip label={`${property.bedrooms} quartos`} size="small" />
                            )}
                            {property.bathrooms > 0 && (
                              <Chip label={`${property.bathrooms} banheiros`} size="small" />
                            )}
                            {property.area && (
                              <Chip label={`${property.area}m²`} size="small" />
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Tab 1: Avaliações */}
        {currentTab === 1 && (
          <Box sx={{ p: 3 }}>
            {reviews.count === 0 ? (
              <Alert severity="info">Este vendedor ainda não possui avaliações.</Alert>
            ) : (
              <Grid container spacing={2}>
                {reviews.results.map((review) => (
                  <Grid size={{ xs: 12 }} key={review.id}>
                    <Paper sx={{ p: 3 }} elevation={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar src={review.reviewer.avatar} sx={{ width: 48, height: 48, mr: 2 }} />
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {review.reviewer.username}
                              {review.reviewer.is_verified && (
                                <Verified sx={{ fontSize: 16, ml: 0.5, color: 'primary.main' }} />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(review.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                        <Rating value={review.rating} readOnly size="small" />
                      </Box>

                      {review.title && (
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {review.title}
                        </Typography>
                      )}

                      <Typography variant="body2" paragraph>
                        {review.comment}
                      </Typography>

                      {review.property_title && (
                        <Chip
                          icon={<Home />}
                          label={`Sobre: ${review.property_title}`}
                          size="small"
                          variant="outlined"
                        />
                      )}

                      {review.seller_response && (
                        <Box sx={{ mt: 2, pl: 3, borderLeft: 3, borderColor: 'primary.main', bgcolor: '#f5f5f5', p: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Resposta do vendedor:
                          </Typography>
                          <Typography variant="body2">{review.seller_response}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(review.seller_response_date)}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SellerProfile;